import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import config from '../../../config/environment';
import { HOW_HEARD_OPTIONS } from '../BookingJourney/steps/HowHeardSurvey';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};

  input, select {
    min-width: 160px;
    padding: 0.55rem 0.7rem;
    border-radius: 8px;
    border: 1px solid rgba(16, 29, 64, 0.18);
    font-size: 0.9rem;
  }
`;

const Button = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.65rem 1rem;
  background: ${props => (props.$secondary ? 'white' : props.theme.colors.primary)};
  color: ${props => (props.$secondary ? props.theme.colors.primary : 'white')};
  border: 1px solid ${props => props.theme.colors.primary};
  font-weight: 600;
  cursor: pointer;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
`;

const StatCard = styled.div`
  background: #f8f7f4;
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  padding: 0.9rem 1rem;

  strong {
    display: block;
    font-size: 1.35rem;
    color: ${props => props.theme.colors.primary};
  }

  span {
    font-size: 0.8rem;
    color: rgba(16, 29, 64, 0.6);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(16, 29, 64, 0.08);

  th, td {
    text-align: left;
    padding: 0.75rem 0.9rem;
    border-bottom: 1px solid rgba(16, 29, 64, 0.06);
    font-size: 0.9rem;
    vertical-align: top;
  }

  th {
    background: #f4f3ef;
    color: ${props => props.theme.colors.primary};
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const Empty = styled.p`
  margin: 1rem 0;
  color: rgba(16, 29, 64, 0.55);
`;

const ErrorText = styled.p`
  color: #c0392b;
  margin: 0;
`;

const HowHeardResponses = () => {
  const today = new Date();
  const fromDefault = new Date(today);
  fromDefault.setDate(fromDefault.getDate() - 30);

  const [dateFrom, setDateFrom] = useState(fromDefault.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));
  const [sourceFilter, setSourceFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
        limit: '1000',
      });
      if (sourceFilter) params.set('source', sourceFilter);
      const res = await fetch(`${config.apiEndpoint}/custom/v1/get-how-heard?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load responses');
      }
      setRows(data.responses || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, sourceFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r.source || 'Unknown';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <Wrap>
      <Toolbar>
        <Field>
          From
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </Field>
        <Field>
          To
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </Field>
        <Field>
          Source
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All</option>
            {counts.map(([src]) => (
              <option key={src} value={src}>{src}</option>
            ))}
            {!counts.length && HOW_HEARD_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </Field>
        <Button type="button" onClick={load}>Refresh</Button>
      </Toolbar>

      {error && <ErrorText>{error}</ErrorText>}

      <Stats>
        <StatCard>
          <strong>{rows.length}</strong>
          <span>Total responses</span>
        </StatCard>
        {counts.slice(0, 6).map(([src, n]) => (
          <StatCard key={src}>
            <strong>{n}</strong>
            <span>{src}</span>
          </StatCard>
        ))}
      </Stats>

      {loading ? (
        <Empty>Loading…</Empty>
      ) : rows.length === 0 ? (
        <Empty>No “how did you hear about us” responses in this date range.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Source</th>
              <th>Other detail</th>
              <th>Booking ref</th>
              <th>Customer</th>
              <th>Page</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.created_at ? new Date(row.created_at).toLocaleString('en-GB') : '—'}</td>
                <td><strong>{row.source}</strong></td>
                <td>{row.other_text || '—'}</td>
                <td>{row.booking_reference || '—'}</td>
                <td>
                  {row.customer_name || '—'}
                  {row.customer_email ? <><br /><span style={{ color: '#666' }}>{row.customer_email}</span></> : null}
                </td>
                <td style={{ maxWidth: 180, wordBreak: 'break-all' }}>{row.page_id || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrap>
  );
};

export default HowHeardResponses;
